import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanyAccount } from "@/hooks/useCompanyAccount";
import { useShipperQuoteRequests, type UnifiedQuoteRow } from "@/hooks/useDirectQuoteRequests";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import type { DateRange } from "react-day-picker";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Sparkles, Search, Calendar as CalendarIcon, Download,
  ChevronLeft, ChevronRight, ArrowRight, X,
} from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { toast } from "sonner";
import { QuoteMetricsRow } from "@/components/lr-direct/QuoteMetricsRow";
import { QuoteAnalyticsPanel } from "@/components/lr-direct/QuoteAnalyticsPanel";
import { LaneAnalyticsPanel } from "@/components/lr-direct/LaneAnalyticsPanel";
import { QuoteRequestsTable } from "@/components/lr-direct/QuoteRequestsTable";


const PAGE_SIZE = 8;

type TabKey = "all" | "open" | "expired" | "converted";

function classifyRow(r: UnifiedQuoteRow): TabKey[] {
  const tabs: TabKey[] = ["all"];
  const now = Date.now();
  const isExpired = new Date(r.deadline).getTime() <= now;
  const s = (r.status || "").toLowerCase();
  if (r.converted_to_load_id) tabs.push("converted");
  else if (isExpired) tabs.push("expired");
  else if (s === "posted" || s === "open") tabs.push("open");
  return tabs;
}

function toCsv(rows: UnifiedQuoteRow[]): string {
  const header = ["Posted","Origin","Destination","Equipment","Status","Quotes","Low","Avg","High","Deadline","ConvertedLoadId"];
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = rows.map((r) => {
    const now = Date.now();
    const isExpired = new Date(r.deadline).getTime() <= now;
    const status = r.converted_to_load_id ? "Converted" : isExpired ? "Expired" : "Open";
    return [
      new Date(r.created_at).toISOString(),
      `${r.origin_city}, ${r.origin_state}`,
      `${r.destination_city}, ${r.destination_state}`,
      r.equipment_type,
      status,
      r.response_count,
      r.low ?? "",
      r.avg != null ? Math.round(r.avg) : "",
      r.high ?? "",
      r.deadline,
      r.converted_to_load_id ?? "",
    ].map(esc).join(",");
  });
  return [header.join(","), ...lines].join("\n");
}

function rowSearchHaystack(r: UnifiedQuoteRow): string {
  const lane = `${r.origin_city}, ${r.origin_state} ${r.destination_city}, ${r.destination_state}`;
  const responderNames = r.responders.map((b) => b.name ?? "").join(" ");
  return [
    r.origin_city,
    r.origin_state,
    r.destination_city,
    r.destination_state,
    lane,
    r.equipment_type,
    r.notes ?? "",
    responderNames,
    r.id,
  ].join(" ").toLowerCase();
}

export default function LRDirectMyQuotesPage() {
  usePageMeta({ title: "My Quotes · LR Direct" });
  const { user } = useAuth();
  const { company } = useCompanyAccount();
  const { rows, loading, refresh } = useShipperQuoteRequests(company?.id, user?.id);
  const navigate = useNavigate();

  // Section-visit clear: mark unread quote_response_received notifications read.
  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from("notifications")
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq("recipient_user_id", user.id)
      .eq("type", "quote_response_received")
      .eq("is_read", false)
      .then(() => { /* noop */ });
  }, [user?.id]);

  const [tab, setTab] = useState<TabKey>("all");
  const [search, setSearch] = useState("");
  const [equipment, setEquipment] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);

  const [deleteRow, setDeleteRow] = useState<UnifiedQuoteRow | null>(null);
  const [convertRow, setConvertRow] = useState<UnifiedQuoteRow | null>(null);
  const [selectedLane, setSelectedLane] = useState<UnifiedQuoteRow | null>(null);
  const [busy, setBusy] = useState(false);


  const counts = useMemo(() => {
    const c: Record<TabKey, number> = { all: 0, open: 0, expired: 0, converted: 0 };
    rows.forEach((r) => classifyRow(r).forEach((t) => (c[t] += 1)));
    return c;
  }, [rows]);

  const equipmentOptions = useMemo(() => {
    const s = new Set(rows.map((r) => r.equipment_type).filter(Boolean));
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);
    const fromMs = dateRange?.from ? new Date(dateRange.from).setHours(0, 0, 0, 0) : null;
    const toMs = dateRange?.to
      ? new Date(dateRange.to).setHours(23, 59, 59, 999)
      : dateRange?.from ? new Date(dateRange.from).setHours(23, 59, 59, 999) : null;
    return rows
      .filter((r) => classifyRow(r).includes(tab))
      .filter((r) => (equipment === "all" ? true : r.equipment_type === equipment))
      .filter((r) => {
        if (fromMs == null) return true;
        const t = new Date(r.created_at).getTime();
        return t >= fromMs && (toMs == null || t <= toMs);
      })
      .filter((r) => {
        if (!q) return true;
        const haystack = rowSearchHaystack(r);
        return tokens.every((token) => haystack.includes(token));
      });
  }, [rows, tab, equipment, search, dateRange]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Real stat-row values
  const stats = useMemo(() => {
    const rowsWithResp = rows.filter((r) => r.response_count > 0 && r.low != null);
    const avgLow = rowsWithResp.length
      ? rowsWithResp.reduce((s, r) => s + (r.low as number), 0) / rowsWithResp.length
      : null;
    const converted = rows.filter((r) => !!r.converted_to_load_id).length;
    const conversionRate = rows.length > 0 ? converted / rows.length : null;
    return {
      total: rows.length,
      open: counts.open,
      avgLow,
      conversionRate,
    };
  }, [rows, counts.open]);

  const handleDelete = async () => {
    if (!deleteRow) return;
    setBusy(true);
    const { error } = await supabase
      .from("direct_loads" as any)
      .update({ deleted_at: new Date().toISOString(), status: "canceled" })
      .eq("id", deleteRow.id);
    setBusy(false); setDeleteRow(null);
    if (error) return toast.error(error.message);
    toast.success("Quote deleted");
    refresh();
  };

  const handleConvert = async () => {
    if (!convertRow) return;
    setBusy(true);
    const { data, error } = await supabase.rpc("convert_direct_quote_to_load" as any, { p_id: convertRow.id });
    setBusy(false); setConvertRow(null);
    if (error) return toast.error(error.message);
    const r = data as { success?: boolean; error?: string; load_number?: string } | null;
    if (!r?.success) return toast.error(r?.error || "Convert failed");
    toast.success(`Converted → ${r.load_number}`);
    refresh();
    navigate("/lr-direct/my-loads?stage=bidding");
  };

  const goDetail = (r: UnifiedQuoteRow) => navigate(`/lr-direct/my-quotes/${r.id}`);
  const goEdit = (r: UnifiedQuoteRow) => navigate(`/lr-direct/post/${r.id}?mode=quote`);
  const goDup = (r: UnifiedQuoteRow) => navigate(`/lr-direct/post?duplicate=${r.id}&mode=quote`);
  const goNew = () => navigate("/lr-direct/post/start?mode=quote");

  const tabs: { key: TabKey; label: string }[] = [
    { key: "all", label: "All Requests" },
    { key: "open", label: "Open" },
    { key: "expired", label: "Expired" },
    { key: "converted", label: "Converted to Load" },
  ];




  return (
    <div className="w-full py-2">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] xl:grid-cols-[minmax(0,1fr)_360px] gap-5">
        {/* MAIN */}
        <div className="min-w-0 space-y-5">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
            <div className="min-w-0 max-w-2xl">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground flex items-center gap-2">
                <Sparkles className="h-6 w-6 sm:h-7 sm:w-7 text-brand-teal" />
                My Quotes
              </h1>
              <p className="mt-1.5 text-sm text-muted-foreground">
                Manage all your freight quote requests and compare broker responses.
              </p>
            </div>
            <Button
              onClick={goNew}
              className="w-full md:w-auto gap-2 text-white h-11 text-sm font-bold shadow-sm"
              style={{ backgroundColor: "hsl(var(--brand-teal))" }}
            >
              <Plus className="h-4 w-4" /> Post New Quote
            </Button>
          </div>

          {/* Stat row — real values only */}
          <QuoteMetricsRow
            totalRequests={stats.total}
            openRequests={stats.open}
            avgLowQuote={stats.avgLow}
            conversionRate={stats.conversionRate}
            hasAnyData={rows.length > 0}
          />

          {/* Tabs */}
          <div className="border-b overflow-x-auto">
            <div className="flex items-center gap-1 min-w-max">
              {tabs.map((t) => {
                const active = tab === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => { setTab(t.key); setPage(1); }}
                    className={`relative px-3 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${
                      active ? "text-brand-teal" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {t.label}
                    <span className={`ml-1.5 text-[11px] font-semibold ${active ? "text-brand-teal" : "text-muted-foreground/70"}`}>
                      {counts[t.key]}
                    </span>
                    {active && (
                      <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-brand-teal" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-2 min-w-0 max-w-full">
            <div className="relative w-full min-w-0 sm:w-auto sm:flex-1 sm:min-w-[220px]">

              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search by origin, destination, lane, equipment, broker…"
                className="pl-8 h-9"
              />
            </div>
            {/* Date range — filters by posted date */}
            <div className="flex items-center">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn("h-9 gap-2", dateRange && "rounded-r-none", !dateRange && "text-muted-foreground")}
                  >
                    <CalendarIcon className="h-4 w-4" />
                    {dateRange?.from ? (
                      dateRange.to
                        ? `${format(dateRange.from, "MMM d")} – ${format(dateRange.to, "MMM d")}`
                        : format(dateRange.from, "MMM d, yyyy")
                    ) : "Date Range"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    selected={dateRange}
                    onSelect={(v) => { setDateRange(v); setPage(1); }}
                    numberOfMonths={2}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
              {dateRange && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 px-2 rounded-l-none border-l-0"
                  onClick={() => { setDateRange(undefined); setPage(1); }}
                  aria-label="Clear date range"
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
            <Select value={equipment} onValueChange={(v) => { setEquipment(v); setPage(1); }}>
              <SelectTrigger className="w-full min-w-0 sm:w-[150px] h-9 text-sm">
                <SelectValue placeholder="Equipment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Equipment</SelectItem>
                {equipmentOptions.map((e) => (
                  <SelectItem key={e} value={e}>{e}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="ml-auto flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-9 gap-2"
                disabled={filtered.length === 0}
                onClick={() => {
                  const csv = toCsv(filtered);
                  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `my-quotes-${new Date().toISOString().slice(0, 10)}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  toast.success(`Exported ${filtered.length} quotes`);
                }}
              >
                <Download className="h-4 w-4" /> Export
              </Button>
            </div>
          </div>

          {/* Table (desktop) / cards (mobile) */}
          {loading ? (
            <Skeleton className="h-64 w-full" />
          ) : filtered.length === 0 ? (
            <Card className="p-10 text-center text-sm text-muted-foreground">
              {rows.length === 0
                ? <>No quote data yet — click <span className="font-semibold text-foreground">+ Post New Quote</span> to request pricing.</>
                : "No quotes match this view."}
            </Card>
          ) : (
            <>
              <div className="hidden md:block">
                <QuoteRequestsTable
                  rows={pageRows}
                  onOpen={goDetail}
                  onEdit={goEdit}
                  onDuplicate={goDup}
                  onDelete={(r) => setDeleteRow(r)}
                  onConvert={(r) => setConvertRow(r)}
                  onAnalytics={(r) => setSelectedLane(r)}
                />
              </div>


              {/* Mobile cards */}
              <div className="md:hidden space-y-2">
                {pageRows.map((r) => (
                  <Card key={`${r.source}-${r.id}`} className="p-3 cursor-pointer" onClick={() => goDetail(r)}>
                    <div className="flex items-center flex-wrap gap-x-2 gap-y-1 font-semibold text-sm">
                      <span className="truncate">{r.origin_city}, {r.origin_state}</span>
                      <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                      <span className="truncate">{r.destination_city}, {r.destination_state}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      <Badge variant="outline" className="text-[10px]">{r.equipment_type}</Badge>
                      <Badge variant="outline" className="text-[10px]">{r.response_count} quotes</Badge>
                      {r.converted_to_load_id && (
                        <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">
                          Converted
                        </Badge>
                      )}
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                      <div><div className="text-[10px] text-muted-foreground">Low</div><div className="font-semibold">{r.low != null ? `$${Math.round(r.low).toLocaleString()}` : "—"}</div></div>
                      <div><div className="text-[10px] text-muted-foreground">Avg</div><div className="font-semibold">{r.avg != null ? `$${Math.round(r.avg).toLocaleString()}` : "—"}</div></div>
                      <div><div className="text-[10px] text-muted-foreground">High</div><div className="font-semibold">{r.high != null ? `$${Math.round(r.high).toLocaleString()}` : "—"}</div></div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
                <div>
                  Showing {(currentPage - 1) * PAGE_SIZE + 1}–
                  {Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length} requests
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline" size="icon" className="h-8 w-8"
                    disabled={currentPage === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  {Array.from({ length: totalPages }).slice(0, 5).map((_, i) => {
                    const n = i + 1;
                    const active = n === currentPage;
                    return (
                      <Button
                        key={n}
                        variant={active ? "default" : "outline"}
                        size="icon"
                        className={`h-8 w-8 ${active ? "bg-brand-teal hover:bg-brand-teal/90 text-white" : ""}`}
                        onClick={() => setPage(n)}
                      >
                        {n}
                      </Button>
                    );
                  })}
                  <Button
                    variant="outline" size="icon" className="h-8 w-8"
                    disabled={currentPage === totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* SIDEBAR */}
        <div className="min-w-0">
          {selectedLane ? (
            <LaneAnalyticsPanel
              selected={selectedLane}
              allRows={rows}
              onClear={() => setSelectedLane(null)}
            />
          ) : (
            <QuoteAnalyticsPanel rows={rows} />
          )}
        </div>

      </div>

      <AlertDialog open={!!deleteRow} onOpenChange={(o) => !o && setDeleteRow(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this quote?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the quote and any broker responses on it. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={busy} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!convertRow} onOpenChange={(o) => !o && setConvertRow(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Convert this quote into a live load?</AlertDialogTitle>
            <AlertDialogDescription>
              This creates a new LR Direct load with the same lane and equipment. Brokers can bid on it. The original quote and all submitted quote responses stay on this page for your reference — nothing is auto-accepted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConvert} disabled={busy}>
              Convert to load
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
